// "Mejorar con IA" de la descripción y el SEO de un producto que ya está en la tienda
// (ADR-2026-10-24-mejorar-con-ia-la-descripcion-y-el-seo).
//
// Es "Completar con IA" (`completarFichaConIa`, el mismo pedido al backend) para UN campo, aunque ya
// tenga texto: el backend recibe lo que hay y lo mejora sin contradecirlo. Como allá, lo que vuelve es
// una propuesta y no un guardado: se ve debajo del campo y entra recién con "Usar esta".
import { useRef, useState } from 'react';
import { Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { boton } from './botones';
import { completarFichaConIa, tieneAlgo, type PedidoDeFicha, type SugerenciaDeFicha } from './fichaIa';
import { useMensajeDeEspera } from './PanelDeIa';

export type CampoMejorable = 'descripcion' | 'seo';

type Estado =
  | { estado: 'pensando' }
  | { estado: 'listo'; sugerencia: SugerenciaDeFicha }
  | { estado: 'error'; error: string };

/**
 * Lo que la IA propuso para cada campo. `armarPedido` da lo que hay hoy en la ficha: se llama al
 * pedir, así "Otra" mejora el texto de ese momento.
 */
export function useMejorarConIa(armarPedido: (campo: CampoMejorable) => PedidoDeFicha) {
  const [porCampo, setPorCampo] = useState<Partial<Record<CampoMejorable, Estado>>>({});
  // Una respuesta que llega después de "Descartar", o de otro pedido, no se muestra.
  const turno = useRef<Record<CampoMejorable, number>>({ descripcion: 0, seo: 0 });

  const pedir = (campo: CampoMejorable) => {
    const mio = ++turno.current[campo];
    setPorCampo((m) => ({ ...m, [campo]: { estado: 'pensando' } }));
    void (async () => {
      const r = await completarFichaConIa(armarPedido(campo));
      if (turno.current[campo] !== mio) return;
      const siguiente: Estado =
        r.sugerencia && tieneAlgo(r.sugerencia, campo)
          ? { estado: 'listo', sugerencia: r.sugerencia }
          : { estado: 'error', error: r.error ?? 'No se le ocurrió nada mejor. Probá de nuevo o escribilo a mano.' };
      setPorCampo((m) => ({ ...m, [campo]: siguiente }));
    })();
  };

  const descartar = (campo: CampoMejorable) => {
    turno.current[campo] += 1;
    setPorCampo((m) => {
      const { [campo]: _fuera, ...resto } = m;
      return resto;
    });
  };

  return { de: (campo: CampoMejorable) => porCampo[campo] ?? null, pedir, descartar };
}

/** El botón chico al lado del título del campo. Mientras piensa, dice qué está haciendo. */
export function BotonMejorarConIa({
  campo,
  pensando,
  onPedir,
}: {
  campo: CampoMejorable;
  pensando: boolean;
  onPedir: () => void;
}) {
  const mensaje = useMensajeDeEspera([campo], pensando);
  return (
    <button
      type="button"
      onClick={onPedir}
      disabled={pensando}
      className="-my-1 inline-flex min-w-0 items-center gap-1 rounded-full py-1 px-2 text-xs font-medium text-brand-primary transition-colors hover:bg-[rgb(var(--brand-primary-rgb)_/_0.07)] disabled:cursor-default disabled:hover:bg-transparent"
    >
      {pensando ? (
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden="true" />
      ) : (
        <Sparkles className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      )}
      {pensando ? (
        <span key={mensaje} className="animate-paso-titulo truncate">
          {mensaje}
        </span>
      ) : (
        'Mejorar con IA'
      )}
    </button>
  );
}

/** La propuesta, debajo del campo: lo que escribiría la IA, con usarla, pedir otra o descartarla. */
export function PropuestaDeIa({
  estado,
  lineas,
  onUsar,
  onOtra,
  onDescartar,
}: {
  estado: Estado | null;
  /** Lo propuesto, una línea por campo (el SEO trae título y descripción). */
  lineas: Array<{ etiqueta?: string; texto: string }>;
  onUsar: () => void;
  onOtra: () => void;
  onDescartar: () => void;
}) {
  if (!estado || estado.estado === 'pensando') return null;
  if (estado.estado === 'error') {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-red-50 py-2 px-3">
        <p className="text-xs text-red-700">{estado.error}</p>
        <span className="flex gap-2">
          <button type="button" onClick={onOtra} className="text-xs font-medium text-red-700 hover:underline">
            Probar de nuevo
          </button>
          <button type="button" onClick={onDescartar} className="text-xs text-red-700/70 hover:underline">
            Cerrar
          </button>
        </span>
      </div>
    );
  }
  return (
    <div role="region" aria-label="Propuesta de la IA" className="space-y-2.5 rounded-xl bg-[rgb(var(--brand-primary-rgb)_/_0.049)] p-3">
      <p className="flex items-center gap-1.5 text-xs font-semibold text-brand-primary">
        <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
        Propuesta de la IA
      </p>
      {lineas.map((l, i) => (
        <div key={i}>
          {l.etiqueta && <p className="text-[11px] font-medium text-neutral-500">{l.etiqueta}</p>}
          <p className="whitespace-pre-wrap text-sm text-brand-black">{l.texto}</p>
        </div>
      ))}
      <p className="text-[11px] text-neutral-500">No se guarda hasta que la uses. Después la podés deshacer.</p>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={onUsar} className={boton('primario', { chico: true })}>
          Usar esta
        </button>
        <button type="button" onClick={onOtra} className={boton('contorno', { chico: true })}>
          <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
          Otra
        </button>
        <button type="button" onClick={onDescartar} className={boton('neutro', { chico: true, extra: 'bg-white' })}>
          Descartar
        </button>
      </div>
    </div>
  );
}
